import { useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import dayjs from 'dayjs';
import { cn } from '../../lib/utils';
import { NavHeader } from './NavHeader';
import { YearPicker } from './YearPicker';

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr',
    'May', 'Jun', 'Jul', 'Aug',
    'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_LABELS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Month view — 3×4 grid of month buttons with year navigation.
 * Clicking the year label drills into an inline YearPicker unless
 * onLabelClick is provided (used by Calendar which manages view state itself).
 *
 * Accessibility: role="grid" semantics with a roving tabIndex. Arrow keys move
 * focus within the 12 months (Left/Right by one, Up/Down by a row), Home/End
 * jump to the first/last month, and PageUp/PageDown step the year.
 */
export function MonthPicker({ selectedDate, onSelect, maxDate, monthsWithData = [], yearsWithData = [], onViewYearChange, onLabelClick }) {
    const [year, setYear] = useState(selectedDate.year());
    const [view, setView] = useState('months'); // 'months' | 'years'
    const [focusedIdx, setFocusedIdx] = useState(selectedDate.month());
    const gridRef = useRef(null);
    const today = dayjs();

    const disableNext = maxDate ? year >= maxDate.year() : false;

    const isMonthDisabled = (idx) =>
        maxDate ? dayjs().year(year).month(idx).isAfter(maxDate, 'month') : false;

    // flushSync commits the state change before we focus, so the target month
    // cell is in the DOM when querySelector runs.
    const focusMonth = (idx) => {
        gridRef.current?.querySelector(`[data-month="${idx}"]`)?.focus();
    };

    const navigateYear = (newYear) => {
        setYear(newYear);
        onViewYearChange?.(dayjs().year(newYear));
    };

    const moveFocus = (idx) => {
        if (idx < 0 || idx > 11 || isMonthDisabled(idx)) return;
        flushSync(() => setFocusedIdx(idx));
        focusMonth(idx);
    };

    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowLeft': moveFocus(focusedIdx - 1); break;
            case 'ArrowRight': moveFocus(focusedIdx + 1); break;
            case 'ArrowUp': moveFocus(focusedIdx - 3); break;
            case 'ArrowDown': moveFocus(focusedIdx + 3); break;
            case 'Home': moveFocus(0); break;
            case 'End': moveFocus(11); break;
            // Month cells are keyed by month name, so the focused cell's DOM node
            // persists across a year change — keyboard focus is retained without
            // an explicit re-focus.
            case 'PageUp': navigateYear(year - 1); break;
            case 'PageDown': if (!disableNext) navigateYear(year + 1); break;
            default: return;
        }
        e.preventDefault();
    };

    // If an external handler is provided (Calendar manages its own view stack),
    // use it; otherwise handle the drill-down internally.
    const handleLabelClick = onLabelClick ?? (() => setView('years'));

    if (view === 'years') {
        return (
            <YearPicker
                key={year}
                selectedDate={dayjs().year(year)}
                onSelect={(yr) => {
                    navigateYear(yr.year());
                    setView('months');
                }}
                maxDate={maxDate}
                yearsWithData={yearsWithData}
            />
        );
    }

    return (
        <div className="p-3 w-[280px]">
            <NavHeader
                label={String(year)}
                onPrev={() => navigateYear(year - 1)}
                onNext={() => navigateYear(year + 1)}
                disableNext={disableNext}
                onLabelClick={handleLabelClick}
                prevLabel="Previous year"
                nextLabel="Next year"
                labelClick="pick a year"
            />
            <div
                ref={gridRef}
                role="grid"
                aria-label={`Months in ${year}`}
                className="mt-4 grid grid-cols-3 gap-2"
                onKeyDown={handleKeyDown}
            >
                {MONTHS.map((label, idx) => {
                    const isSelected =
                        selectedDate.year() === year && selectedDate.month() === idx;
                    const isCurrentMonth =
                        today.year() === year && today.month() === idx;
                    const isDisabled = isMonthDisabled(idx);
                    const monthKey = `${year}-${String(idx + 1).padStart(2, '0')}`;
                    const isHighlighted = monthsWithData.includes(monthKey);

                    return (
                        <div key={label} role="gridcell" aria-selected={isSelected} className="contents">
                            <button
                                type="button"
                                tabIndex={idx === focusedIdx ? 0 : -1}
                                onClick={() =>
                                    onSelect(dayjs().year(year).month(idx).startOf('month'))
                                }
                                disabled={isDisabled}
                                aria-disabled={isDisabled}
                                aria-current={isCurrentMonth ? 'date' : undefined}
                                aria-label={`${MONTH_LABELS[idx]} ${year}`}
                                data-month={idx}
                                className={cn(
                                    'relative flex h-9 flex-col items-center justify-center rounded-md text-sm transition-colors',
                                    'hover:bg-accent/20 hover:text-foreground',
                                    isSelected &&
                                        'bg-accent text-accent-foreground hover:bg-accent',
                                    isCurrentMonth && !isSelected && 'bg-muted text-foreground',
                                    isDisabled && 'pointer-events-none opacity-30'
                                )}
                            >
                                <span className={isHighlighted ? '-translate-y-px' : ''}>
                                    {label}
                                </span>
                                {isHighlighted && (
                                    <span
                                        aria-hidden="true"
                                        className={cn(
                                            'absolute bottom-1 h-1 w-1 rounded-full',
                                            isSelected ? 'bg-accent-foreground' : 'bg-accent'
                                        )}
                                    />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
